import React from 'react'
import { styled } from '@mui/system'
import { colors, fontSizes, BORDER_RADIUSES } from '../consts'
import { Snackbar } from '../LazyComponents'

type FirebaseErrorSnackbarProps = {
  show: boolean
  onClose?: () => void
  onRefresh?: () => void
}

const ErrorSnackbarContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: `rgba(${colors.red}, 0.95)`,
  width: '50vw',
  maxWidth: 500,
  borderRadius: BORDER_RADIUSES.medium,
  padding: theme.spacing(3, 2),
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  [theme.breakpoints.down('sm')]: {
    width: '90vw',
    padding: theme.spacing(2.5, 1.5),
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  zIndex: 99999,
}))

const ErrorMessage = styled('div')(({ theme }) => ({
  flex: 1,
  '& p': {
    fontSize: fontSizes.medium,
    fontWeight: 700,
    color: colors.white,
    textAlign: 'left',
    textShadow: '0 0 2px rgba(0, 0, 0, 0.5)',
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: fontSizes.small,
      textAlign: 'center',
    },
  },
}))

const RefreshButton = styled('button')(({ theme }) => ({
  background: colors.white,
  color: colors.red,
  border: 'none',
  borderRadius: BORDER_RADIUSES.small,
  padding: theme.spacing(1, 2),
  fontSize: fontSizes.small,
  fontWeight: 700,
  cursor: 'pointer',
  minWidth: 80,
  transition: 'all 0.2s ease',
  '&:hover': {
    background: colors.lightestGrey,
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    padding: theme.spacing(1.5, 2),
  },
}))

export const FirebaseErrorSnackbar: React.FC<FirebaseErrorSnackbarProps> = ({
  show,
  onClose,
  onRefresh,
}) => {
  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    onClose?.()
  }

  const handleRefresh = () => {
    onRefresh?.()
    onClose?.()
  }

  return (
    <Snackbar
      autoHideDuration={null}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      open={show}
      onClose={handleClose}
      role="alert"
      aria-live="polite"
    >
      <ErrorSnackbarContainer role="status">
        <ErrorMessage>
          <p>Connection error detected. Please refresh to continue.</p>
        </ErrorMessage>
        <RefreshButton
          onClick={handleRefresh}
          aria-label="Refresh page to fix connection error"
        >
          Refresh
        </RefreshButton>
      </ErrorSnackbarContainer>
    </Snackbar>
  )
}
